import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Ship, Download } from 'lucide-react';
import {
  CONTAINER_STATUSES,
  daysFromNow,
  etaLabel,
  fmtDate,
  portByCode,
  statusLabel,
  type Company,
} from '../../data/claudeMockData';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDirection } from '@/contexts/DirectionContext';
import {
  formatEtaHeading,
  formatEtaSubtext,
} from '@/features/containers/constants/directionLabels';
import { RouteCell, CompanyBadge } from './shared';
import type { UpcomingContainerRow } from '../../interfaces/upcomingContainers.interface';
import type { BookingSummaryRow } from '../../interfaces/bookings.interface';
import type { PortTransitTimeRow } from '../../interfaces/portTransitTimes.interface';
import type { LatestOfferRow } from '../../interfaces/latestOffers.interface';
import type { CurrentOfferPrices } from '../../interfaces/currentOfferPrices.interface';
import type { PortDeparturesRow } from '../../interfaces/vesselDepartures.interface';
import { useOfferCards } from '@/features/offers/hooks/useOfferCards';
import { OfferCardModal } from '@/features/offers/components/OfferCardModal';
import type { OfferCard } from '@/features/offers/interfaces/offer-card.interface';
import { exportXlsx, type ExcelColumn } from '@/utils/exportXlsx';

interface Props {
  companyId: string;
}

/**
 * Construiește un Company sintetic pentru CompanyBadge folosind datele venite
 * din backend (companyName/companyId), fără să depindă de lista hard-codată.
 */
function buildCompanyForBadge(name: string, id: number): Company | undefined {
  if (!name) return undefined;
  const palette = ['#1e6bd6', '#0c8a5b', '#b6500f', '#7a3aa8', '#0f7a8a', '#c0392b'];
  const color = palette[id % palette.length] || palette[0];
  const short = name
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase())
    .join('')
    .slice(0, 3) || name.slice(0, 3).toUpperCase();
  return { id: `co_${id}`, name, short, color, vat: '' };
}

/* ============================================================
   Upcoming Containers (span-2 card cu tabel)
   ============================================================ */
interface UpcomingContainersCardProps {
  data: UpcomingContainerRow[];
  isLoading?: boolean;
}

export function UpcomingContainersCard({ data, isLoading }: UpcomingContainersCardProps) {
  const { currentLanguage: lang } = useLanguage();
  const navigate = useNavigate();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  // Pe export: ETA la portul cunoscut (final = POD, altfel intermediar).
  // Pe import: ETA Constanța.
  const isExport = direction === 'export';
  const etaColumnLabel = isExport ? formatEtaHeading(direction, lang) : 'ETA';

  // Implicit: export → On Board desc (recente primele); import → ETA asc (apropiate primele).
  // Backend sortează la fel; re-sortăm client-side ca fallback / consistență.
  const rows = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      if (isExport) {
        const ta = a.onBoardDate ? new Date(a.onBoardDate).getTime() : null;
        const tb = b.onBoardDate ? new Date(b.onBoardDate).getTime() : null;
        if (ta == null && tb == null) return 0;
        if (ta == null) return 1;
        if (tb == null) return -1;
        return tb - ta;
      }
      const ta = a.eta ? new Date(a.eta).getTime() : null;
      const tb = b.eta ? new Date(b.eta).getTime() : null;
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb;
    });
    return copy;
  }, [data, isExport]);

  const renderEmpty = () => (
    <div className="muted fs-13" style={{ padding: '24px 16px', textAlign: 'center' }}>
      {isLoading ? (isRo ? 'Se încarcă…' : 'Loading…') : isRo ? 'Niciun container activ.' : 'No active containers.'}
    </div>
  );

  // Export Excel — toate coloanele tabelului + cele afișate doar ca text secundar (tip, port ETA).
  const handleExport = () => {
    if (rows.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const columns: ExcelColumn<UpcomingContainerRow>[] = [
      { header: isRo ? 'Container' : 'Container', value: (r) => r.containerNumber || '', width: 16 },
      { header: isRo ? 'Tip' : 'Type', value: (r) => r.containerType || '', width: 8 },
      { header: 'Booking', value: (r) => r.bookingNumber || '', width: 14 },
      { header: isRo ? 'Firmă' : 'Company', value: (r) => r.companyName || '', width: 26 },
      { header: 'Shipper', value: (r) => r.shipperName || '', width: 28 },
      {
        header: isRo ? 'Rută' : 'Route',
        value: (r) => (r.pol || r.pod ? `${r.pol || '—'} → ${r.pod || '—'}` : ''),
        width: 16,
      },
      { header: 'POL', value: (r) => r.pol || '', width: 8 },
      { header: 'POD', value: (r) => r.pod || '', width: 8 },
      { header: 'On Board Date', value: (r) => (r.onBoardDate ? new Date(r.onBoardDate) : null), width: 14 },
      { header: etaColumnLabel, value: (r) => (r.eta ? new Date(r.eta) : null), width: 14 },
      {
        header: isRo ? 'Port ETA' : 'ETA port',
        value: (r) => [r.etaPort, r.etaPortName].filter(Boolean).join(' · '),
        width: 22,
      },
      {
        header: isRo ? 'Status' : 'Status',
        value: (r) => statusLabel(CONTAINER_STATUSES, r.status, lang) || r.status,
        width: 18,
      },
    ];
    exportXlsx<UpcomingContainerRow>({
      fileName: `containere-${isExport ? 'pleaca' : 'vin'}-in-curand-${today}`,
      sheetName: isExport
        ? isRo
          ? 'Pleacă în curând'
          : 'Departing soon'
        : isRo
          ? 'Vin în curând'
          : 'Arriving soon',
      rows,
      columns,
    });
  };

  return (
    <div className="card span-2" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="card-header">
        <div>
          <div className="title">
            {isExport
              ? isRo
                ? 'Containere care pleacă în curând'
                : 'Containers departing soon'
              : isRo
              ? 'Containere care vin în curând'
              : 'Containers arriving soon'}
          </div>
          <div className="sub">
            {isRo
              ? `Toate containerele active (${rows.length})`
              : `All active containers (${rows.length})`}
          </div>
        </div>
        <div className="row gap-2" style={{ alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={handleExport}
            disabled={rows.length === 0}
            title={rows.length === 0 ? (isRo ? 'Nimic de exportat' : 'Nothing to export') : undefined}
          >
            <Download size={13} />
            {isRo ? 'Export' : 'Export'}
          </button>
          <button
            type="button"
            className="link accent"
            onClick={() => navigate('/app/containers')}
          >
            {isRo ? 'Vezi toate' : 'View all'} <ArrowRight size={12} />
          </button>
        </div>
      </div>
      <div className="table-wrap hidden-mobile" style={{ flex: 1, minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="data">
          <thead>
            <tr>
              <th>{isRo ? 'Container' : 'Container'}</th>
              <th>{isRo ? 'Firmă' : 'Company'}</th>
              <th>Shipper</th>
              <th>{isRo ? 'Rută' : 'Route'}</th>
              <th>On Board Date</th>
              <th>{etaColumnLabel}</th>
              <th>{isRo ? 'Status' : 'Status'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8}>{renderEmpty()}</td>
              </tr>
            )}
            {rows.map((c) => {
              const st = CONTAINER_STATUSES[c.status];
              const co = buildCompanyForBadge(c.companyName, c.companyId);
              return (
                <tr key={`${c.id}-${c.containerNumber}`} className="clickable">
                  <td>
                    <div className="mono fw-600 fs-13">{c.containerNumber || '—'}</div>
                    <div className="muted fs-11 mt-1">{c.containerType}</div>
                  </td>
                  <td>
                    <CompanyBadge company={co} />
                  </td>
                  <td>
                    <span
                      className="fs-13"
                      title={c.shipperName || ''}
                      style={{
                        display: 'inline-block',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'bottom',
                      }}
                    >
                      {c.shipperName || '—'}
                    </span>
                  </td>
                  <td>
                    <RouteCell pol={c.pol} pod={c.pod} />
                  </td>
                  <td className="muted fs-13" style={{ whiteSpace: 'nowrap' }}>
                    {c.onBoardDate ? fmtDate(c.onBoardDate, lang) : '—'}
                  </td>
                  <td style={{ minWidth: 132, whiteSpace: 'nowrap' }}>
                    <div className="fw-500 fs-13">{c.eta ? fmtDate(c.eta, lang) : '—'}</div>
                    <div className="muted fs-11">
                      {c.eta
                        ? isExport
                          ? formatEtaSubtext(direction, lang, c) ||
                            formatEtaHeading(direction, lang, c)
                          : etaLabel(c.eta, lang)
                        : ''}
                    </div>
                  </td>
                  <td>
                    {st ? (
                      <span className={'badge ' + st.badge}>
                        <span className="dot" />
                        {statusLabel(CONTAINER_STATUSES, c.status, lang)}
                      </span>
                    ) : (
                      <span className="muted fs-11">{c.status}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="show-mobile" style={{ flex: 1, minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {rows.length === 0 && renderEmpty()}
        {rows.map((c) => {
          const st = CONTAINER_STATUSES[c.status];
          const co = buildCompanyForBadge(c.companyName, c.companyId);
          return (
            <div
              key={`${c.id}-${c.containerNumber}`}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                className="row"
                style={{ justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="mono fw-600 fs-13">{c.containerNumber || '—'}</div>
                  <div className="muted fs-11 mt-1">{c.containerType}</div>
                  {c.shipperName ? (
                    <div className="fs-11 mt-1" title={c.shipperName}>
                      Shipper: {c.shipperName}
                    </div>
                  ) : null}
                </div>
                {st ? (
                  <span className={'badge ' + st.badge}>
                    <span className="dot" />
                    {statusLabel(CONTAINER_STATUSES, c.status, lang)}
                  </span>
                ) : (
                  <span className="muted fs-11">{c.status}</span>
                )}
              </div>
              <div className="row gap-2" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div className="row gap-2">
                  <CompanyBadge company={co} />
                  <RouteCell pol={c.pol} pod={c.pod} />
                </div>
                <div className="muted fs-12 text-right">
                  {c.onBoardDate ? (
                    <div className="muted fs-11">OBD {fmtDate(c.onBoardDate, lang)}</div>
                  ) : null}
                  {isExport ? formatEtaHeading(direction, lang, c) : etaColumnLabel}{' '}
                  <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {c.eta ? fmtDate(c.eta, lang) : '—'}
                  </span>
                  {c.eta && !isExport && <span> · {etaLabel(c.eta, lang)}</span>}
                  {c.eta && isExport && formatEtaSubtext(direction, lang, c) ? (
                    <div className="muted fs-11">{formatEtaSubtext(direction, lang, c)}</div>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Vessel Departures
   Import: top 4 POL × 2 plecări · Export: următoarele 8 din Constanța
   ============================================================ */
interface VesselDeparturesCardProps {
  data: PortDeparturesRow[];
  isLoading?: boolean;
}

export function VesselDeparturesCard({ data, isLoading }: VesselDeparturesCardProps) {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport = direction === 'export';

  // Aplatizăm grupurile × plecări într-o listă unică.
  const items = data.flatMap((portGroup) =>
    portGroup.departures.map((d) => ({
      portCode: portGroup.portCode,
      portName: portGroup.portName,
      ...d,
    })),
  );

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="title">{isRo ? 'Plecări nave' : 'Vessel departures'}</div>
          <div className="sub">
            {isExport
              ? isRo
                ? 'Următoarele 8 plecări din Constanța'
                : 'Next 8 departures from Constanța'
              : isRo
                ? 'Următoarele 2 plecări din top 4 porturi de origine'
                : 'Next 2 departures from your top 4 origin ports'}
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 12px 12px' }}>
        {isLoading && items.length === 0 && (
          <div className="muted fs-13" style={{ padding: '16px', textAlign: 'center' }}>
            {isRo ? 'Se încarcă…' : 'Loading…'}
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="muted fs-13" style={{ padding: '16px', textAlign: 'center' }}>
            {isRo ? 'Nu există plecări programate.' : 'No scheduled departures.'}
          </div>
        )}
        {items.map((d, i) => {
          const fromPort = portByCode(d.portCode);
          const dDays = d.etd ? daysFromNow(d.etd) : 999;

          // Export: doar nava + ETD din Constanța (fără hub / POD).
          // Import: origine → Istanbul → Constanța.
          const routeLine = isExport
            ? isRo
              ? 'Plecare din Constanța'
              : 'Departure from Constanța'
            : `${fromPort?.name || d.portName || d.portCode} → Istanbul → Constanța`;

          return (
            <div
              key={`${d.portCode}-${d.vesselCode || i}-${d.etd || i}`}
              style={{
                padding: '10px 8px',
                borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: 'var(--surface-2)',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--accent)',
                  flexShrink: 0,
                }}
              >
                <Ship size={18} strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="fw-600 fs-13"
                  style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {d.vesselName || '—'}
                  {d.vesselCode && (
                    <span className="muted fw-500" style={{ fontSize: 11 }}>
                      {' '}
                      · {d.vesselCode}
                    </span>
                  )}
                </div>
                <div className="muted fs-12" style={{ marginTop: 2 }}>
                  {routeLine}
                  {!isExport && d.service ? (
                    <span className="muted fs-11"> · {d.service}</span>
                  ) : null}
                  {isExport && d.service ? (
                    <span className="muted fs-11"> · {d.service}</span>
                  ) : null}
                </div>
              </div>
              <div className="text-right" style={{ flexShrink: 0 }}>
                <div
                  className="fw-600 fs-13"
                  style={{ color: dDays <= 3 ? 'var(--st-warn)' : 'var(--text)' }}
                >
                  {d.etd ? fmtDate(d.etd, lang) : '—'}
                </div>
                <div className="muted fs-11">{d.etd ? etaLabel(d.etd, lang) : ''}</div>
                {!isExport && d.transshipmentEta ? (
                  <div className="muted fs-11" style={{ marginTop: 2 }}>
                    IST {fmtDate(d.transshipmentEta, lang)}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Bookings — „Bookingurile în tranzit"
   ============================================================ */
interface BookingsCardProps {
  data: BookingSummaryRow[];
  isLoading?: boolean;
}

type BookingsSortKey = 'ref' | 'company' | 'route' | 'vessel' | 'etd' | 'onBoard' | 'eta' | 'status';
type BookingsSortDir = 'asc' | 'desc';

const BookingsSortHeader: React.FC<{
  id: BookingsSortKey;
  label: string;
  sortKey: BookingsSortKey;
  sortDir: BookingsSortDir;
  onSort: (k: BookingsSortKey) => void;
}> = ({ id, label, sortKey, sortDir, onSort }) => {
  const isSorted = sortKey === id;
  return (
    <th
      className={'sortable' + (isSorted ? ' sorted' : '')}
      onClick={() => onSort(id)}
    >
      {label}
      <span className="sort-ind">{isSorted ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};

/** Valoare sortabilă; pentru date returnează null când lipsește (null-uri mereu la final). */
function bookingSortValue(row: BookingSummaryRow, key: BookingsSortKey): string | number | null {
  switch (key) {
    case 'ref':
      return (row.ref || '').toLowerCase();
    case 'company':
      return (row.companyName || '').toLowerCase();
    case 'route':
      return `${row.pol || ''}|${row.pod || ''}`.toLowerCase();
    case 'vessel':
      return (row.vesselName || '').toLowerCase();
    case 'etd':
      return row.etd ? new Date(row.etd).getTime() : null;
    case 'onBoard':
      return row.onBoardDate ? new Date(row.onBoardDate).getTime() : null;
    case 'eta':
      return row.eta ? new Date(row.eta).getTime() : null;
    case 'status':
      return (row.status || '').toLowerCase();
    default:
      return '';
  }
}

function defaultBookingsSort(isExport: boolean): { key: BookingsSortKey; dir: BookingsSortDir } {
  return isExport ? { key: 'onBoard', dir: 'desc' } : { key: 'eta', dir: 'asc' };
}

export function BookingsCard({ data, isLoading }: BookingsCardProps) {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport = direction === 'export';
  const initial = defaultBookingsSort(isExport);
  const [sortKey, setSortKey] = useState<BookingsSortKey>(initial.key);
  const [sortDir, setSortDir] = useState<BookingsSortDir>(initial.dir);

  // La schimbarea direcției, revenim la sortarea implicită (export OBD desc / import ETA asc).
  useEffect(() => {
    const next = defaultBookingsSort(direction === 'export');
    setSortKey(next.key);
    setSortDir(next.dir);
  }, [direction]);

  const onSort = (k: BookingsSortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // On Board: default DESC (recente primele); restul date/text: ASC
      setSortDir(k === 'onBoard' ? 'desc' : 'asc');
    }
  };

  const sorted = useMemo(() => {
    const rows = [...data];
    rows.sort((a, b) => {
      const va = bookingSortValue(a, sortKey);
      const vb = bookingSortValue(b, sortKey);
      // Null-uri (date lipsă) mereu la final, indiferent de direcție.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  // Export Excel — aceleași coloane ca tabelul afișat (ordinea sortată curent).
  const handleExport = () => {
    if (sorted.length === 0) return;
    const today = new Date().toISOString().slice(0, 10);
    const columns: ExcelColumn<BookingSummaryRow>[] = [
      {
        header: 'Ref.',
        value: (r) =>
          r.containersSummary ? `${r.ref || ''} · ${r.containersSummary}` : r.ref || '',
        width: 28,
      },
      { header: isRo ? 'Firmă' : 'Company', value: (r) => r.companyName || '', width: 26 },
      {
        header: isRo ? 'Rută' : 'Route',
        value: (r) => (r.pol || r.pod ? `${r.pol || '—'} → ${r.pod || '—'}` : ''),
        width: 16,
      },
      { header: isRo ? 'Navă' : 'Vessel', value: (r) => r.vesselName || '', width: 18 },
      { header: 'ETD', value: (r) => (r.etd ? new Date(r.etd) : null), width: 12 },
      { header: 'On Board Date', value: (r) => (r.onBoardDate ? new Date(r.onBoardDate) : null), width: 14 },
      { header: 'ETA', value: (r) => (r.eta ? new Date(r.eta) : null), width: 12 },
      {
        header: isRo ? 'Status' : 'Status',
        value: (r) => statusLabel(CONTAINER_STATUSES, r.status, lang) || r.status,
        width: 16,
      },
    ];
    exportXlsx<BookingSummaryRow>({
      fileName: `bookinguri-in-tranzit-${today}`,
      sheetName: isRo ? 'În tranzit' : 'In transit',
      rows: sorted,
      columns,
    });
  };

  const renderEmpty = () => (
    <div className="muted fs-13" style={{ padding: '24px 16px', textAlign: 'center' }}>
      {isLoading
        ? isRo
          ? 'Se încarcă…'
          : 'Loading…'
        : isRo
          ? 'Niciun booking în tranzit.'
          : 'No bookings in transit.'}
    </div>
  );

  return (
    <div className="card span-2" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="card-header">
        <div>
          <div className="title">
            {isRo ? 'Bookingurile în tranzit' : 'Bookings in transit'}
          </div>
          <div className="sub">
            {isRo
              ? `Rezervări active, nefinalizate (${data.length})`
              : `Active, non-completed bookings (${data.length})`}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          onClick={handleExport}
          disabled={data.length === 0}
          title={
            data.length === 0
              ? isRo
                ? 'Nimic de exportat'
                : 'Nothing to export'
              : undefined
          }
        >
          <Download size={13} />
          {isRo ? 'Export' : 'Export'}
        </button>
      </div>
      <div className="table-wrap hidden-mobile" style={{ flex: 1, minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="data">
          <thead>
            <tr>
              <BookingsSortHeader id="ref" label="Ref." sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <BookingsSortHeader
                id="company"
                label={isRo ? 'Firmă' : 'Company'}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <BookingsSortHeader
                id="route"
                label={isRo ? 'Rută' : 'Route'}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <BookingsSortHeader
                id="vessel"
                label={isRo ? 'Navă' : 'Vessel'}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <BookingsSortHeader id="etd" label="ETD" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <BookingsSortHeader
                id="onBoard"
                label="On Board Date"
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
              <BookingsSortHeader id="eta" label="ETA" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <BookingsSortHeader
                id="status"
                label={isRo ? 'Status' : 'Status'}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
              />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan={8}>{renderEmpty()}</td>
              </tr>
            )}
            {sorted.map((b) => {
              const st = CONTAINER_STATUSES[b.status];
              const co = buildCompanyForBadge(b.companyName, b.companyId);
              return (
                <tr key={b.id}>
                  <td>
                    <div className="mono fw-600 fs-13">{b.ref}</div>
                    <div className="muted fs-11 mt-1">{b.containersSummary}</div>
                  </td>
                  <td>
                    <CompanyBadge company={co} />
                  </td>
                  <td>
                    <RouteCell pol={b.pol} pod={b.pod} />
                  </td>
                  <td>
                    <div className="fw-500 fs-13" style={{ whiteSpace: 'nowrap' }}>
                      {b.vesselName || '—'}
                    </div>
                  </td>
                  <td className="muted fs-13" style={{ whiteSpace: 'nowrap' }}>{b.etd ? fmtDate(b.etd, lang) : '—'}</td>
                  <td className="muted fs-13" style={{ whiteSpace: 'nowrap' }}>
                    {b.onBoardDate ? fmtDate(b.onBoardDate, lang) : '—'}
                  </td>
                  <td className="fw-500 fs-13" style={{ whiteSpace: 'nowrap' }}>{b.eta ? fmtDate(b.eta, lang) : '—'}</td>
                  <td>
                    {st ? (
                      <span className={'badge ' + st.badge}>
                        <span className="dot" />
                        {statusLabel(CONTAINER_STATUSES, b.status, lang)}
                      </span>
                    ) : (
                      <span className="muted fs-11">{b.status}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="show-mobile" style={{ flex: 1, minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {sorted.length === 0 && renderEmpty()}
        {sorted.map((b) => {
          const st = CONTAINER_STATUSES[b.status];
          const co = buildCompanyForBadge(b.companyName, b.companyId);
          return (
            <div
              key={b.id}
              style={{
                padding: '12px 16px',
                borderTop: '1px solid var(--border)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div className="row" style={{ justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="mono fw-600 fs-13">{b.ref}</div>
                  <div className="muted fs-11 mt-1">{b.containersSummary} · {b.vesselName || '—'}</div>
                </div>
                {st ? (
                  <span className={'badge ' + st.badge}>
                    <span className="dot" />
                    {statusLabel(CONTAINER_STATUSES, b.status, lang)}
                  </span>
                ) : (
                  <span className="muted fs-11">{b.status}</span>
                )}
              </div>
              <div className="row gap-2" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div className="row gap-2">
                  <CompanyBadge company={co} />
                  <RouteCell pol={b.pol} pod={b.pod} />
                </div>
              </div>
              <div className="muted fs-11" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>
                  ETD <span style={{ color: 'var(--text)', fontWeight: 500 }}>{b.etd ? fmtDate(b.etd, lang) : '—'}</span>
                </span>
                <span>
                  OBD{' '}
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>
                    {b.onBoardDate ? fmtDate(b.onBoardDate, lang) : '—'}
                  </span>
                </span>
                <span>
                  ETA <span style={{ color: 'var(--text)', fontWeight: 500 }}>{b.eta ? fmtDate(b.eta, lang) : '—'}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Current Prices — navlu din oferta validă astăzi
   Fiecare element apare ca un rând cu prețuri pe coloane pe tip container.
   ============================================================ */
interface CurrentPricesCardProps {
  data: CurrentOfferPrices | null | undefined;
  isLoading?: boolean;
}

export function CurrentPricesCard({ data, isLoading }: CurrentPricesCardProps) {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const isRo = lang === 'ro';
  const isExport =
    direction === 'export' ||
    data?.direction === 'export' ||
    (data?.offerType || '').toLowerCase().includes('export');

  const renderEmpty = (msg: string) => (
    <div className="muted fs-13" style={{ padding: '24px 16px', textAlign: 'center' }}>
      {msg}
    </div>
  );

  const containerTypes = data?.containerTypes || [];
  const rows = data?.rows || [];
  const currencyCode = data?.currency || '';
  const currencySym = data?.currencySymbol || (currencyCode === 'EUR' ? '€' : currencyCode === 'USD' ? '$' : '');
  const showVat = !!data?.navluVat;

  const formatCell = (v: number | null) => {
    if (v == null) return '';
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '';
    const formatted = n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
    // Ca în preview export: „850 USD + TVA"
    const code = currencyCode || currencySym || '';
    return `${formatted}${code ? ` ${code}` : ''}${showVat ? (isRo ? ' + TVA' : ' + VAT') : ''}`;
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="card-header">
        <div>
          <div className="title">{isRo ? 'Ultima ofertă' : 'Latest offer'}</div>
          <div className="sub">
            {data
              ? isRo
                ? `${data.companyName || '—'} · valabilă până la ${data.validityEndDate ? fmtDate(data.validityEndDate, lang) : '—'}`
                : `${data.companyName || '—'} · valid until ${data.validityEndDate ? fmtDate(data.validityEndDate, lang) : '—'}`
              : isRo
                ? 'Tarife din oferta valabilă astăzi'
                : "Rates from today's valid offer"}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoading && !data && renderEmpty(isRo ? 'Se încarcă…' : 'Loading…')}
        {!isLoading &&
          !data &&
          renderEmpty(isRo ? 'Nicio ofertă validă astăzi.' : 'No offer valid today.')}
        {data &&
          rows.length === 0 &&
          renderEmpty(
            isRo ? 'Oferta nu conține linii de navlu.' : 'Offer has no freight lines.',
          )}
        {data && rows.length > 0 && (
          <table className="data" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{isExport ? (isRo ? 'Prestație' : 'Service') : isRo ? 'Rută' : 'Route'}</th>
                <th>{isRo ? 'Firmă' : 'Company'}</th>
                {containerTypes.map((ct) => (
                  <th key={ct} style={{ textAlign: 'right' }}>
                    {ct}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.elementId}>
                  <td>
                    {isExport ? (
                      <span className="fw-500 fs-13" style={{ lineHeight: 1.35 }}>
                        {r.name || '—'}
                      </span>
                    ) : r.pol || r.pod ? (
                      <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                        <span className="mono fw-500 fs-12">{r.pol || '—'}</span>
                        <ArrowRight size={11} strokeWidth={2} />
                        <span className="mono fw-500 fs-12">{r.pod || '—'}</span>
                      </div>
                    ) : (
                      <span className="fw-500 fs-13">{r.name || '—'}</span>
                    )}
                  </td>
                  <td className="fs-12 fw-500">{data.companyName || '—'}</td>
                  {containerTypes.map((_, idx) => {
                    const cell = formatCell(r.prices[idx] ?? null);
                    return (
                      <td key={idx} className="text-right mono fw-600 fs-13">
                        {cell || '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   Transit Times
   ============================================================ */
interface TransitTimesCardProps {
  data: PortTransitTimeRow[];
  isLoading?: boolean;
}

export function TransitTimesCard({ data, isLoading }: TransitTimesCardProps) {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';

  // Bara este normalizată la cel mai lung tranzit observat (sau 35 zile minim
  // ca să nu apară bare la 100% pe rute scurte când singurul punct e mic).
  const maxT = Math.max(35, ...data.map((d) => d.averageTransitTime || 0));

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="card-header">
        <div>
          <div className="title">{isRo ? 'Timpi de tranzit' : 'Transit times'}</div>
          <div className="sub">
            {isRo
              ? 'Medii calculate din rezervările tale finalizate'
              : 'Averages from your completed bookings'}
          </div>
        </div>
      </div>
      <div style={{ padding: '8px 0', flex: 1, minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {isLoading && data.length === 0 && (
          <div className="muted fs-13" style={{ padding: '24px 20px', textAlign: 'center' }}>
            {isRo ? 'Se încarcă…' : 'Loading…'}
          </div>
        )}
        {!isLoading && data.length === 0 && (
          <div className="muted fs-13" style={{ padding: '24px 20px', textAlign: 'center' }}>
            {isRo
              ? 'Nu există rezervări finalizate cu date suficiente pentru calcul.'
              : 'No completed bookings with enough data to compute transit times.'}
          </div>
        )}
        {data.map((r, i) => {
          const w = Math.max(4, (r.averageTransitTime / maxT) * 100);
          return (
            <div
              key={`${r.polCode}-${r.podCode}`}
              style={{
                padding: '12px 20px',
                borderBottom: i === data.length - 1 ? 'none' : '1px solid var(--border)',
              }}
            >
              <div className="row gap-2 mb-2" style={{ justifyContent: 'space-between' }}>
                <div className="row gap-2" style={{ minWidth: 0 }}>
                  <span className="fw-600 fs-13">{r.polName}</span>
                  <ArrowRight size={12} strokeWidth={2} />
                  <span className="fw-600 fs-13">{r.podName}</span>
                </div>
                <div className="mono fw-600 fs-13">
                  {r.averageTransitTime} {isRo ? 'zile' : 'days'}
                </div>
              </div>
              <div
                style={{
                  width: '100%',
                  height: 6,
                  background: 'var(--surface-2)',
                  borderRadius: 99,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${w}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, var(--accent) 0%, var(--accent-saffron) 100%)',
                    borderRadius: 99,
                  }}
                />
              </div>
              <div className="row gap-2 muted fs-11 mt-2">
                <span>
                  {r.bookingsCount} {isRo
                    ? r.bookingsCount === 1
                      ? 'rezervare'
                      : 'rezervări'
                    : r.bookingsCount === 1
                    ? 'booking'
                    : 'bookings'}
                </span>
                <span>·</span>
                <span>
                  {r.containersCount}{' '}
                  {isRo
                    ? r.containersCount === 1
                      ? 'container'
                      : 'containere'
                    : r.containersCount === 1
                    ? 'container'
                    : 'containers'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Latest Offers — redesign pentru model multi-item (navlu + locale + auto)
   O ofertă conține mai multe `offerElements`, cu prețuri diferite per tip
   container. Afișăm range de preț (min..max), număr de elemente pe tip și
   rute distincte din elementele de tip navlu.
   ============================================================ */
interface LatestOffersCardProps {
  data: LatestOfferRow[];
  isLoading?: boolean;
}

/**
 * Backend întoarce statusul de afișare derivat din validitate:
 * „Validă" / „Expirată" (vezi dashboard.service.getLatestOffers).
 */
const STATUS_BADGE_MAP: Record<string, string> = {
  'validă': 'badge-success',
  valida: 'badge-success',
  'expirată': 'badge-neutral',
  expirata: 'badge-neutral',
};

function statusBadgeClass(status: string): string {
  if (!status) return 'badge-neutral';
  return STATUS_BADGE_MAP[status.toLowerCase()] || 'badge-neutral';
}

function formatPriceRange(min: number | null, max: number | null, currency: string | null): string {
  if (min == null || max == null) return '';
  const cur = currency || 'USD';
  if (min === max) return `${cur} ${min.toLocaleString()}`;
  return `${cur} ${min.toLocaleString()} – ${max.toLocaleString()}`;
}

function formatValidity(start: string | null, end: string | null, lang: 'ro' | 'en'): string {
  if (!start && !end) return lang === 'ro' ? 'fără termen' : 'no validity';
  const s = start ? fmtDate(start, lang) : '—';
  const e = end ? fmtDate(end, lang) : '—';
  return `${s} → ${e}`;
}

export function LatestOffersCard({ data, isLoading }: LatestOffersCardProps) {
  const { currentLanguage: lang } = useLanguage();
  const { direction } = useDirection();
  const navigate = useNavigate();
  const isRo = lang === 'ro';
  const isExportDir = direction === 'export';

  // Reutilizăm cardurile complete de pe pagina /offers (același cache) ca să
  // putem deschide modalul direct din dashboard. Dacă oferta nu e în cache
  // (ex. filtru de firmă diferit), facem fallback la navigare.
  const { data: offerCards } = useOfferCards();
  const [activeOffer, setActiveOffer] = useState<OfferCard | null>(null);

  const openOffer = (id: number) => {
    const card = offerCards?.find((c) => c.id === id);
    if (card) setActiveOffer(card);
    else navigate(`/app/offers/${id}`);
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="card-header">
        <div>
          <div className="title">{isRo ? 'Ultimele oferte' : 'Latest offers'}</div>
          <div className="sub">
            {isRo
              ? 'Oferte recente — fiecare poate conține mai multe elemente'
              : 'Recent offers — each may contain multiple line items'}
          </div>
        </div>
        <button type="button" className="link accent" onClick={() => navigate('/app/offers')}>
          {isRo ? 'Vezi toate' : 'View all'} <ArrowRight size={12} />
        </button>
      </div>
      <div style={{ padding: 12, flex: 1, minHeight: 0, maxHeight: '60vh', overflowY: 'auto' }}>
        {isLoading && data.length === 0 && (
          <div className="muted fs-13" style={{ padding: '12px 4px', textAlign: 'center' }}>
            {isRo ? 'Se încarcă…' : 'Loading…'}
          </div>
        )}
        {!isLoading && data.length === 0 && (
          <div className="muted fs-13" style={{ padding: '12px 4px', textAlign: 'center' }}>
            {isRo ? 'Nicio ofertă disponibilă.' : 'No offers available.'}
          </div>
        )}
        {data.map((o) => {
          const co = buildCompanyForBadge(o.companyName, o.companyId);
          const badgeClass = statusBadgeClass(o.status);
          const priceRange = formatPriceRange(o.priceMin, o.priceMax, o.shippingCurrency);
          const validity = formatValidity(o.validityStartDate, o.validityEndDate, lang);
          return (
            <div
              key={o.id}
              role="button"
              tabIndex={0}
              onClick={() => openOffer(o.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') openOffer(o.id);
              }}
              style={{
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                marginBottom: 6,
                background: 'var(--bg)',
                cursor: 'pointer',
                display: 'flex',
                gap: 12,
                alignItems: 'stretch',
              }}
            >
              {/* Coloana stângă: info principale */}
              <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="fw-600 fs-13">{o.type || (isRo ? 'Ofertă' : 'Offer')}</span>
                  <span className="muted fs-11">·</span>
                  <span className="muted fs-11">{validity}</span>
                </div>
                <div className="row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  {co && <CompanyBadge company={co} />}
                  {o.containerTypes.slice(0, 6).map((ct) => (
                    <span
                      key={ct}
                      className="mono fs-11"
                      style={{
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'var(--surface-2)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {ct}
                    </span>
                  ))}
                  {o.containerTypes.length > 6 && (
                    <span className="muted fs-11">+{o.containerTypes.length - 6}</span>
                  )}
                </div>
                {o.routes.length > 0 && (
                  <div
                    className="muted fs-11"
                    style={{
                      lineHeight: 1.35,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={
                      o.routes.length > 1
                        ? `${o.routes[0]} (+${o.routes.length - 1})`
                        : o.routes[0]
                    }
                  >
                    {o.routes[0]}
                    {o.routes.length > 1 && (
                      <span style={{ color: 'var(--text-muted)' }}>
                        {' '}
                        +{o.routes.length - 1}{' '}
                        {isRo
                          ? o.routes.length - 1 === 1
                            ? 'element'
                            : 'elemente'
                          : o.routes.length - 1 === 1
                            ? 'item'
                            : 'items'}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Coloana dreaptă: status, preț, counts */}
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 8,
                  minWidth: 0,
                }}
              >
                {o.status && (
                  <span className={'badge ' + badgeClass}>
                    <span className="dot" />
                    {o.status}
                  </span>
                )}
                {priceRange && (
                  <div className="mono fw-600 fs-13" style={{ whiteSpace: 'nowrap' }}>
                    {priceRange}
                  </div>
                )}
                <div className="muted fs-11" style={{ whiteSpace: 'nowrap' }}>
                  {/* Export: fără navlu/locale/auto (în ofertă e all-in). Import: detaliu pe tip. */}
                  {isExportDir || (o.type || '').toLowerCase().includes('export') ? (
                    (() => {
                      const n = o.navluCount || o.routes?.length || 0;
                      if (n <= 0) {
                        return <span>{isRo ? 'fără elemente' : 'no items'}</span>;
                      }
                      return (
                        <>
                          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{n}</span>
                          {isRo
                            ? n === 1
                              ? ' element ofertat'
                              : ' elemente ofertate'
                            : n === 1
                              ? ' quoted item'
                              : ' quoted items'}
                        </>
                      );
                    })()
                  ) : (
                    <>
                      {o.navluCount > 0 && (
                        <>
                          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{o.navluCount}</span> navlu
                        </>
                      )}
                      {o.localeCount > 0 && (
                        <>
                          {o.navluCount > 0 && ' · '}
                          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{o.localeCount}</span>{' '}
                          {isRo ? 'locale' : 'local'}
                        </>
                      )}
                      {o.autoCount > 0 && (
                        <>
                          {(o.navluCount > 0 || o.localeCount > 0) && ' · '}
                          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{o.autoCount}</span> auto
                        </>
                      )}
                      {o.elementsTotal === 0 && (
                        <span>{isRo ? 'fără elemente' : 'no items'}</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <OfferCardModal
        isOpen={activeOffer !== null}
        offer={activeOffer}
        onClose={() => setActiveOffer(null)}
      />
    </div>
  );
}
